import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { workspaceApi } from '../api/apiClient';
import PersonalPrivacyLockIcon from '../components/PersonalPrivacyLockIcon';
import './WorkspacesPage.css';

function FolderIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3.5 6.75A2.75 2.75 0 0 1 6.25 4h3.09a2 2 0 0 1 1.41.59l1.6 1.6h5.4a2.75 2.75 0 0 1 2.75 2.75v8A2.75 2.75 0 0 1 17.75 19h-11A2.75 2.75 0 0 1 4 16.25V7.5a.75.75 0 0 0-.5-.75Z" />
    </svg>
  );
}

export default function WorkspacesPage() {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newColor, setNewColor] = useState('#DC2626');
  const [newPersonalPrivate, setNewPersonalPrivate] = useState(false);
  const navigate = useNavigate();

  const colors = ['#DC2626', '#2563EB', '#059669', '#7C3AED', '#D97706', '#0891B2'];

  useEffect(() => {
    loadWorkspaces();
  }, []);

  const loadWorkspaces = async () => {
    try {
      const res = await workspaceApi.list();
      setWorkspaces(res.data);
    } catch (err) {
      console.error('Failed to load workspaces', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await workspaceApi.create({
        name: newName,
        description: newDesc,
        color: newColor,
        personalPrivate: newPersonalPrivate,
      });
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      setNewPersonalPrivate(false);
      loadWorkspaces();
    } catch (err) {
      console.error('Failed to create workspace', err);
    }
  };

  // ASA-203: verwijderen is (net als removeMember) OWNER-only in de backend; een
  // workspace-ADMIN kreeg wel de knop maar een 400 terug. Alleen OWNER tonen.
  // (Systeem-admins beheren spaces via de aparte /admin/workspaces-view.)
  const canDeleteWorkspace = (workspace) =>
    workspace?.userRole === 'OWNER';

  const handleDeleteWorkspace = async (workspace, event) => {
    event.stopPropagation();
    if (!canDeleteWorkspace(workspace) || deletingId) {
      return;
    }

    const confirmed = window.confirm(
      `Delete knowledge space "${workspace.name}"?\n\nThis action cannot be undone.`
    );
    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(workspace.id);
      await workspaceApi.delete(workspace.id);
      setWorkspaces((prev) => prev.filter((w) => w.id !== workspace.id));
    } catch (err) {
      console.error('Failed to delete workspace', err);
      // ASA-203: toon de concrete serverreden i.p.v. de generieke fallback.
      const reason = err?.response?.data?.message;
      window.alert(reason
        ? `Could not delete workspace: ${reason}`
        : 'Failed to delete workspace. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="v-loading">Loading workspaces...</div>;
  }

  return (
    <div className="v-page">
      <div className="v-page__header">
        <div>
          <h1 className="v-page__title">Knowledge Spaces</h1>
          <p className="v-page__subtitle">
            Only knowledge spaces you own or are a member of are shown here. Create a space to upload files and collaborate on evidence-backed answers.
          </p>
        </div>
        <button
          className="v-btn v-btn--primary"
          onClick={() => {
            setNewPersonalPrivate(false);
            setShowCreate(true);
          }}
        >
          + New knowledge space
        </button>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="v-modal-backdrop" onClick={() => setShowCreate(false)}>
          <div className="v-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create knowledge space</h3>
            <form onSubmit={handleCreate}>
              <div className="v-form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Product Manuals"
                  required
                  autoFocus
                />
              </div>
              <div className="v-form-group">
                <label>Description</label>
                <textarea
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="What will this workspace be used for?"
                  rows={3}
                />
              </div>
              <div className="v-form-group">
                <label>Color</label>
                <div className="v-color-picker">
                  {colors.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`v-color-swatch ${c === newColor ? 'v-color-swatch--active' : ''}`}
                      style={{ background: c }}
                      onClick={() => setNewColor(c)}
                    />
                  ))}
                </div>
              </div>
              <div className="v-form-group">
                <label className="v-inline-checkbox v-inline-checkbox--privacy">
                  <input
                    type="checkbox"
                    checked={newPersonalPrivate}
                    onChange={(e) => setNewPersonalPrivate(e.target.checked)}
                  />
                  <PersonalPrivacyLockIcon size="sm" title="Higher privacy workspace" />
                  <span>Higher privacy (personal): hide this workspace from platform admins unless they are members</span>
                </label>
              </div>
              <div className="v-modal__actions">
                <button type="button" className="v-btn v-btn--ghost" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="v-btn v-btn--primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Workspace grid */}
      {workspaces.length === 0 ? (
        <div className="v-empty">
          <div className="v-empty__icon"><FolderIcon /></div>
          <h3>No knowledge spaces yet</h3>
          <p>Create your first space to upload documents and build a searchable knowledge base.</p>
          <button className="v-btn v-btn--primary" onClick={() => setShowCreate(true)}>
            + Create knowledge space
          </button>
        </div>
      ) : (
        <div className="v-workspace-grid">
          {workspaces.map((ws) => (
            <div
              key={ws.id}
              className="v-workspace-card"
              onClick={() => navigate(`/workspaces/${ws.id}`)}
            >
              <div className="v-workspace-card__header">
                <div className="v-workspace-card__icon" style={{ background: ws.color || '#DC2626' }}>
                  {ws.name?.charAt(0)?.toUpperCase()}
                </div>
                <span className="v-workspace-card__role">{ws.userRole}</span>
              </div>
              <h3 className="v-workspace-card__name">
                {ws.name}
                {ws.personalPrivate && (
                  <PersonalPrivacyLockIcon size="md" title="Personal-private workspace" />
                )}
              </h3>
              <p className="v-workspace-card__desc">{ws.description || 'No description'}</p>
              <div className="v-workspace-card__meta">
                <span>{ws.documentCount} document{ws.documentCount !== 1 ? 's' : ''}</span>
                <span>•</span>
                <span>{ws.memberCount} member{ws.memberCount !== 1 ? 's' : ''}</span>
              </div>
              {canDeleteWorkspace(ws) && (
                <div className="v-workspace-card__actions">
                  <button
                    type="button"
                    className="v-btn v-btn--danger v-btn--small"
                    onClick={(e) => handleDeleteWorkspace(ws, e)}
                    disabled={deletingId === ws.id}
                  >
                    {deletingId === ws.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
